import React, { useState, useEffect } from "react";
import { useParams, useSearchParams, useLocation, useNavigate } from "react-router-dom";
import {
  Box,
  Button,
  Typography,
  Stack,
  TextField,
  Alert,
  CircularProgress,
  Paper,
  Stepper,
  Step,
  StepLabel,
  IconButton,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import PhoneInput from "react-phone-input-2";
import "react-phone-input-2/lib/material.css";

const API_BASE = process.env.REACT_APP_API_BASE_URL;

const STEPS = ["Date & Travelers", "Your Details", "Review & Pay"];

function fmtDate(d) {
  return new Intl.DateTimeFormat("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  }).format(new Date(d));
}

function fmtSlot(s) {
  const start = fmtDate(s.startDate);
  const end = fmtDate(s.endDate);
  return start === end ? start : `${start} – ${end}`;
}

const SectionLabel = ({ children }) => (
  <Typography
    sx={{
      fontSize: "0.72rem",
      fontWeight: 700,
      color: "text.secondary",
      textTransform: "uppercase",
      letterSpacing: 0.6,
      mb: 1.5,
    }}
  >
    {children}
  </Typography>
);

const TourBookingForm = () => {
  const { tourId } = useParams();
  const [searchParams] = useSearchParams();
  const distributorId =
    searchParams.get("distributor") || searchParams.get("distributorId");
  const location = useLocation();
  const passedTour = location.state?.tour;
  const navigate = useNavigate();

  const [tour, setTour] = useState(passedTour || null);
  const [loadingTour, setLoadingTour] = useState(!passedTour);
  const [loadError, setLoadError] = useState("");

  // wizard state
  const [step, setStep] = useState(0);
  const [slotIdx, setSlotIdx] = useState(null);
  const [travelers, setTravelers] = useState(1);
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [errors, setErrors] = useState({});
  const [apiError, setApiError] = useState("");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (passedTour) return;
    const fetchTour = async () => {
      try {
        const res = await fetch(
          `${API_BASE}/api/widget/${distributorId}?tourId=${tourId}`
        );
        if (!res.ok) throw new Error("Tour not found");
        const data = await res.json();
        const found = Array.isArray(data) ? data[0] : data;
        if (!found) throw new Error("Tour not found for this distributor");
        setTour(found);
      } catch (err) {
        setLoadError(err.message || "Failed to load tour");
      } finally {
        setLoadingTour(false);
      }
    };
    fetchTour();
  }, [tourId, distributorId, passedTour]);

  if (loadingTour) {
    return (
      <Box display="flex" justifyContent="center" mt={8}>
        <CircularProgress />
      </Box>
    );
  }

  if (!tour) {
    return (
      <Box textAlign="center" mt={5} px={2}>
        <Alert severity="error" sx={{ mb: 2 }}>
          {loadError || "Failed to load tour."}
        </Alert>
        <Button startIcon={<ArrowBackIcon />} onClick={() => navigate(-1)}>
          Go back
        </Button>
      </Box>
    );
  }

  const slots = tour.availability || [];
  const slot = slotIdx !== null ? slots[slotIdx] : null;
  const tourPrice = tour.price ?? tour.pricing?.pricePerPerson ?? 0;
  const tourCurrency = tour.currency || tour.pricing?.currency || "NGN";
  const tourName = tour.title || tour.basicInfo?.tour_name || "Tour";
  const total = +(tourPrice * travelers).toFixed(2);

  function validateStep0() {
    const e = {};
    if (slotIdx === null && slots.length > 0)
      e.slot = "Please select an availability window";
    if (travelers < 1) e.travelers = "At least 1 traveler required";
    setErrors(e);
    return Object.keys(e).length === 0;
  }

  function validateStep1() {
    const e = {};
    if (!name.trim()) e.name = "Full name is required";
    if (!email.trim() || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email))
      e.email = "Valid email is required";
    if (!phone.trim()) e.phone = "Phone number is required";
    setErrors(e);
    return Object.keys(e).length === 0;
  }

  function next() {
    if (step === 0 && !validateStep0()) return;
    if (step === 1 && !validateStep1()) return;
    setErrors({});
    setStep((s) => s + 1);
  }

  async function pay() {
    setSubmitting(true);
    setApiError("");
    try {
      const date =
        slot?.startDate ?? new Date().toISOString().split("T")[0];
      const res = await fetch(`${API_BASE}/api/payment/initialize`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          name,
          email,
          phone,
          date,
          travelers,
          tourId,
          distributorId,
        }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || "Payment initialization failed");
      window.location.href = data.authUrl;
    } catch (err) {
      setApiError(err.message || "Something went wrong");
      setSubmitting(false);
    }
  }

  const reviewRows = [
    ["Tour", tourName],
    slot ? ["Date window", fmtSlot(slot)] : null,
    ["Travelers", travelers],
    [
      "Price per person",
      `${tourCurrency} ${Number(tourPrice).toLocaleString()}`,
    ],
    ["Name", name],
    ["Email", email],
    ["Phone", phone ? `+${phone}` : "—"],
  ].filter(Boolean);

  return (
    <Box sx={{ maxWidth: 640, mx: "auto", px: { xs: 2, sm: 3 }, py: { xs: 3, sm: 5 } }}>
      <Button
        startIcon={<ArrowBackIcon />}
        onClick={() => navigate(-1)}
        sx={{ mb: 3 }}
      >
        Back
      </Button>

      <Typography variant="h5" fontWeight={800} gutterBottom sx={{ letterSpacing: -0.3 }}>
        {tourName}
      </Typography>
      <Typography variant="body2" color="text.secondary" mb={4}>
        From {tourCurrency} {Number(tourPrice).toLocaleString()} per person
      </Typography>

      <Stepper activeStep={step} sx={{ mb: 5 }}>
        {STEPS.map((label) => (
          <Step key={label}>
            <StepLabel>{label}</StepLabel>
          </Step>
        ))}
      </Stepper>

      {/* Step 0 — Date & Travelers */}
      {step === 0 && (
        <Stack spacing={4}>
          <Box>
            <SectionLabel>Select availability window</SectionLabel>
            {slots.length === 0 ? (
              <Paper
                variant="outlined"
                sx={{
                  p: 3,
                  textAlign: "center",
                  color: "text.secondary",
                  borderStyle: "dashed",
                  borderRadius: 2,
                }}
              >
                No availability windows have been defined for this tour.
              </Paper>
            ) : (
              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
                  gap: 1.5,
                }}
              >
                {slots.map((s, i) => (
                  <Paper
                    key={i}
                    variant="outlined"
                    onClick={() => setSlotIdx(i)}
                    sx={{
                      p: 2,
                      cursor: "pointer",
                      borderRadius: 2,
                      borderWidth: slotIdx === i ? 2 : 1,
                      borderColor: slotIdx === i ? "#E50000" : "divider",
                      bgcolor: slotIdx === i ? "#fff5f5" : "background.paper",
                      transition: "all 0.15s",
                      "&:hover": { borderColor: "#E50000", bgcolor: "#fff5f5" },
                    }}
                  >
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      display="block"
                      mb={0.25}
                    >
                      Window {i + 1}
                    </Typography>
                    <Typography fontWeight={600} fontSize="0.9rem">
                      {fmtSlot(s)}
                    </Typography>
                  </Paper>
                ))}
              </Box>
            )}
            {errors.slot && (
              <Typography color="error" fontSize="0.78rem" mt={0.75}>
                {errors.slot}
              </Typography>
            )}
          </Box>

          <Box>
            <SectionLabel>Number of travelers</SectionLabel>
            <Stack direction="row" alignItems="center" spacing={2}>
              <IconButton
                onClick={() => setTravelers((t) => Math.max(1, t - 1))}
                disabled={travelers <= 1}
                sx={{
                  border: "1.5px solid",
                  borderColor: "divider",
                  width: 38,
                  height: 38,
                }}
              >
                <span style={{ fontSize: 18, fontWeight: 700, lineHeight: 1 }}>−</span>
              </IconButton>
              <Typography fontWeight={700} fontSize="1.15rem" minWidth={28} textAlign="center">
                {travelers}
              </Typography>
              <IconButton
                onClick={() => setTravelers((t) => t + 1)}
                sx={{
                  border: "1.5px solid",
                  borderColor: "divider",
                  width: 38,
                  height: 38,
                }}
              >
                <span style={{ fontSize: 18, fontWeight: 700, lineHeight: 1 }}>+</span>
              </IconButton>
            </Stack>
            {errors.travelers && (
              <Typography color="error" fontSize="0.78rem" mt={0.75}>
                {errors.travelers}
              </Typography>
            )}
          </Box>
        </Stack>
      )}

      {/* Step 1 — Personal Details */}
      {step === 1 && (
        <Stack spacing={2.5}>
          <TextField
            label="Full Name"
            fullWidth
            value={name}
            onChange={(e) => setName(e.target.value)}
            error={!!errors.name}
            helperText={errors.name}
          />
          <TextField
            label="Email Address"
            type="email"
            fullWidth
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            error={!!errors.email}
            helperText={errors.email}
          />
          <Box>
            <PhoneInput
              country="ng"
              value={phone}
              onChange={(val) => setPhone(val)}
              inputStyle={{ width: "100%" }}
            />
            {errors.phone && (
              <Typography color="error" fontSize="0.78rem" mt={0.75}>
                {errors.phone}
              </Typography>
            )}
          </Box>
        </Stack>
      )}

      {/* Step 2 — Review & Pay */}
      {step === 2 && (
        <Stack spacing={0}>
          {apiError && (
            <Alert severity="error" sx={{ mb: 2.5 }}>
              {apiError}
            </Alert>
          )}

          <Paper variant="outlined" sx={{ borderRadius: 2, overflow: "hidden" }}>
            {reviewRows.map(([label, val], i) => (
              <Box
                key={i}
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "flex-start",
                  gap: 2,
                  px: 2.5,
                  py: 1.5,
                  borderBottom: i < reviewRows.length - 1 ? "1px solid" : "none",
                  borderColor: "divider",
                }}
              >
                <Typography variant="body2" color="text.secondary" flexShrink={0}>
                  {label}
                </Typography>
                <Typography variant="body2" fontWeight={600} textAlign="right">
                  {val}
                </Typography>
              </Box>
            ))}
          </Paper>

          <Paper
            sx={{
              mt: 2,
              px: 2.5,
              py: 2,
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              bgcolor: "#fff5f5",
              border: "1px solid #fecaca",
              borderRadius: 2,
            }}
          >
            <Typography fontWeight={700}>Total</Typography>
            <Typography fontWeight={800} fontSize="1.15rem" color="#E50000">
              {tourCurrency} {Number(total).toLocaleString()}
            </Typography>
          </Paper>
        </Stack>
      )}

      {/* Navigation */}
      <Stack direction="row" spacing={1.5} mt={5}>
        {step > 0 && (
          <Button
            variant="outlined"
            onClick={() => setStep((s) => s - 1)}
            disabled={submitting}
            sx={{ flex: 1 }}
          >
            Back
          </Button>
        )}
        {step < 2 ? (
          <Button
            variant="contained"
            disableElevation
            onClick={next}
            sx={{
              flex: 2,
              bgcolor: "#E50000",
              "&:hover": { bgcolor: "#C00000" },
            }}
          >
            Continue
          </Button>
        ) : (
          <Button
            variant="contained"
            disableElevation
            onClick={pay}
            disabled={submitting}
            sx={{
              flex: 2,
              bgcolor: "#E50000",
              "&:hover": { bgcolor: "#C00000" },
            }}
          >
            {submitting ? "Redirecting…" : "Pay Now"}
          </Button>
        )}
      </Stack>
    </Box>
  );
};

export default TourBookingForm;
